import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/Header'
import { useAuth } from '../hooks/useAuth'
import './PublicCanteenVoting.css'

interface CanteenMenuItem {
  id: string
  date: string
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  menu_number?: number
  name: string
  description?: string
  allergens?: string
  total_food_votes?: number
  average_food_rating?: number
  total_service_votes?: number
  average_service_rating?: number
  food_rating_1_count?: number
  food_rating_2_count?: number
  food_rating_3_count?: number
  food_rating_4_count?: number
  food_rating_5_count?: number
  service_rating_1_count?: number
  service_rating_2_count?: number
  service_rating_3_count?: number
  service_rating_4_count?: number
  service_rating_5_count?: number
  can_vote?: boolean
  // user_has_voted удалено - теперь нет защиты от повторов, можно голосовать сколько угодно
}

interface MenuData {
  menu: CanteenMenuItem[]
  days_limit: number
  date_range: {
    start: string
    end: string
  }
}

export default function PublicCanteenVoting() {
  const navigate = useNavigate()
  const { user } = useAuth()
  
  // Statistiky: správce jídelny, ředitel, IT admin
  const canSeeCanteenStats =
    user?.role === 'spravce_jidelny' ||
    user?.role === 'director' ||
    user?.role === 'admin'
  const [menuData, setMenuData] = useState<MenuData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Для модального окна отзыва
  const [voteModalOpen, setVoteModalOpen] = useState(false)
  const [selectedMenuItem, setSelectedMenuItem] = useState<CanteenMenuItem | null>(null)
  const [foodRating, setFoodRating] = useState(0)
  const [serviceRating, setServiceRating] = useState(0)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadMenu()
  }, [])

  const loadMenu = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin + '/api'
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 секунд timeout
      
      const response = await fetch(`${apiUrl}/public-canteen/menu`, {
        signal: controller.signal,
        cache: 'no-store',
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        if (response.status === 503) {
          throw new Error('Server je momentálně nedostupný. Zkuste to později.')
        }
        throw new Error('Nepodařilo se načíst menu')
      }
      
      const data: MenuData = await response.json()
      
      // Валидация данных
      if (!data || !Array.isArray(data.menu)) {
        throw new Error('Neplatná data z serveru')
      }
      
      setMenuData(data)
    } catch (err: any) {
      if (err.name === 'AbortError') {
        setError('Timeout: Server nepodařilo se načíst menu včas')
      } else {
        setError(err.message || 'Nepodařilo se načíst menu')
      }
    } finally {
      setLoading(false)
    }
  }

  const [selectedLunchVariant, setSelectedLunchVariant] = useState<1 | 2 | null>(null)
  const [lunchVariants, setLunchVariants] = useState<{lunch1?: CanteenMenuItem, lunch2?: CanteenMenuItem}>({})

  const openVoteModal = (menuItem: CanteenMenuItem) => {
    // Если это обед, находим оба варианта для этого дня
    if (menuItem.meal_type === 'lunch') {
      const dayMenu = menuData?.menu.filter(m => m.date === menuItem.date && m.meal_type === 'lunch') || []
      const lunch1 = dayMenu.find(m => m.menu_number === 1)
      const lunch2 = dayMenu.find(m => m.menu_number === 2)
      
      setLunchVariants({ lunch1, lunch2 })
      setSelectedMenuItem(menuItem)
      setSelectedLunchVariant(menuItem.menu_number as 1 | 2)
    } else {
      setSelectedMenuItem(menuItem)
      setLunchVariants({})
      setSelectedLunchVariant(null)
    }
    
    setFoodRating(0)
    setServiceRating(0)
    setComment('')
    setVoteModalOpen(true)
  }

  const closeVoteModal = () => {
    setVoteModalOpen(false)
    setSelectedMenuItem(null)
    setLunchVariants({})
    setSelectedLunchVariant(null)
    setFoodRating(0)
    setServiceRating(0)
    setComment('')
  }

  const submitVote = async () => {
    if (!selectedMenuItem || foodRating === 0 || serviceRating === 0) {
      alert('Prosím, ohodnoťte kvalitu jídla i obsluhy')
      return
    }
    
    // Для обеда проверяем что выбран вариант
    if (selectedMenuItem.meal_type === 'lunch' && !selectedLunchVariant) {
      alert('Prosím, vyberte variant obědu (1 nebo 2)')
      return
    }

    // Валидация комментария
    if (comment.length > 500) {
      alert('Komentář je příliš dlouhý (max 500 znaků)')
      return
    }

    setSubmitting(true)

    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin + '/api'
      
      // Для обеда используем ID выбранного варианта
      let voteMenuId = selectedMenuItem.id
      if (selectedMenuItem.meal_type === 'lunch' && selectedLunchVariant) {
        const selectedVariant = selectedLunchVariant === 1 ? lunchVariants.lunch1 : lunchVariants.lunch2
        if (selectedVariant) {
          voteMenuId = selectedVariant.id
        }
      }
      
      // Проверка honeypot поля
      const honeypotField = document.getElementById('website') as HTMLInputElement
      const honeypotValue = honeypotField?.value || ''
      
      const response = await fetch(`${apiUrl}/public-canteen/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          menu_id: voteMenuId,
          food_quality_rating: foodRating,
          service_quality_rating: serviceRating,
          comment: comment.trim() || undefined,
          website: honeypotValue
        })
      })

      const result = await response.json()

      if (!response.ok) {
        // Специальная обработка для rate limiting
        if (response.status === 429) {
          throw new Error('Příliš mnoho recenzí za hodinu. Zkuste to později.')
        }
        throw new Error(result.error || 'Nepodařilo se odeslat recenzi')
      }

      alert('✅ Recenze byla úspěšně odeslána!')
      closeVoteModal()
      await loadMenu() // Обновляем меню
    } catch (err: any) {
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        alert('❌ Chyba: Problém s připojením k serveru')
      } else {
        alert('❌ Chyba: ' + (err.message || 'Nepodařilo se odeslat recenzi'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const getMealTypeName = (type: string): string => {
    const names: Record<string, string> = {
      soup: 'Polévka',
      breakfast: 'Snídaně',
      lunch: 'Oběd',
      dinner: 'Večeře',
      snack: 'Svačina'
    }
    return names[type] || type
  }

  const getMealTypeIcon = (type: string): string => {
    const icons: Record<string, string> = {
      soup: '🍲',
      breakfast: '🍳',
      lunch: '🍽️',
      dinner: '🌙',
      snack: '🥪'
    }
    return icons[type] || '🍴'
  }

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00')
    const days = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota']
    return `${days[date.getDay()]}, ${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
  }

  const getDateStatus = (dateStr: string): 'today' | 'past' => {
    const date = new Date(dateStr + 'T00:00:00')
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    date.setHours(0, 0, 0, 0)
    
    if (date.getTime() === today.getTime()) return 'today'
    return 'past'
  }

  // Функция renderStars удалена так как не используется

  const getRatingLabel = (rating: number): string => {
    const labels: Record<number, string> = {
      1: 'Velmi špatné',
      2: 'Špatné',
      3: 'Průměrné',
      4: 'Dobré',
      5: 'Výborné'
    }
    return labels[rating] || ''
  }

  if (loading) {
    return (
      <div className="public-canteen-voting">
        <Header
          leftButtons={
            <>
              <button
                onClick={() => navigate(-1)}
                className="back-button"
              >
                ← Zpět
              </button>
              <h1 style={{ margin: 0, fontSize: '1.5rem' }}>🍽️ Hodnocení školní jídelny</h1>
            </>
          }
          showLogo={true}
        />
        <div className="canteen-container">
          <div className="loading">Načítání menu...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="public-canteen-voting">
        <Header
          leftButtons={
            <>
              <button
                onClick={() => navigate(-1)}
                className="back-button"
              >
                ← Zpět
              </button>
              <h1 style={{ margin: 0, fontSize: '1.5rem' }}>🍽️ Hodnocení školní jídelny</h1>
            </>
          }
          showLogo={true}
        />
        <div className="canteen-container">
          <div className="error-message">
            ❌ {error}
          </div>
        </div>
      </div>
    )
  }

  if (!menuData || menuData.menu.length === 0) {
    return (
      <div className="public-canteen-voting">
        <Header
          leftButtons={
            <>
              <button
                onClick={() => navigate(-1)}
                className="back-button"
              >
                ← Zpět
              </button>
              <h1 style={{ margin: 0, fontSize: '1.5rem' }}>🍽️ Hodnocení školní jídelny</h1>
            </>
          }
          showLogo={true}
        />
        <div className="canteen-container">
          <p className="subtitle" style={{ textAlign: 'center', marginTop: '1rem' }}>Veřejné hodnocení bez registrace</p>
          <div className="no-data">
            <p>📭 Zatím není k dispozici žádné menu pro hodnocení</p>
            <p className="hint">Menu se aktualizuje automaticky každý den v 7:00</p>
          </div>
        </div>
      </div>
    )
  }

  // Определяем, является ли пользователь студентом
  // Если user не определен (гость без OAuth), считаем что это не студент
  const isStudent = user?.user_type === 'student'
  
  // Фильтруем меню: гости/сотрудники видят только обеды, студенты видят все
  // Важно: если user не определен (null/undefined), это гость - показываем только обеды
  const filteredMenu = isStudent 
    ? menuData.menu 
    : menuData.menu.filter(item => item.meal_type === 'lunch')

  // Группируем меню по датам и объединяем обеды в одну карточку
  const menuByDate = filteredMenu.reduce((acc, item) => {
    if (!acc[item.date]) {
      acc[item.date] = []
    }
    acc[item.date].push(item)
    return acc
  }, {} as Record<string, CanteenMenuItem[]>)

  // Группируем обеды в одну карточку для отображения
  const displayMenuByDate: Record<string, CanteenMenuItem[]> = {}
  
  // Функция для создания карточки обеда
  const createLunchCard = (lunches: CanteenMenuItem[], date: string): CanteenMenuItem | null => {
    if (lunches.length === 0) return null
    
    // Убрали проверку hasVotedForAnyLunch - теперь можно голосовать сколько угодно
    const canVoteForLunch = lunches.some(lunch => lunch.can_vote)
    
    return {
      ...lunches[0],
      id: `lunch-${date}`,
      name: 'Oběd',
      description: `${lunches.length} varianty`,
      menu_number: 0,
      can_vote: canVoteForLunch
    }
  }
  
  Object.keys(menuByDate).forEach(date => {
    const items = menuByDate[date]
    const display: CanteenMenuItem[] = []
    
    // Порядок отображения: Snídaně, Oběd (посередине), Svačina, Večeře
    // Но только если пользователь - студент, иначе только обеды
    const breakfast = items.filter(i => i.meal_type === 'breakfast')
    const lunches = items.filter(i => i.meal_type === 'lunch')
    const snacks = items.filter(i => i.meal_type === 'snack')
    const dinners = items.filter(i => i.meal_type === 'dinner')
    
    // Для студентов: обед всегда посередине
    if (isStudent) {
      // Добавляем завтрак
      display.push(...breakfast)
      
      // Добавляем обед - ПОСЕРЕДИНЕ
      const lunchCard = createLunchCard(lunches, date)
      if (lunchCard) {
        display.push(lunchCard)
      }
      
      // Добавляем остальное
      display.push(...snacks)
      display.push(...dinners)
    } else {
      // Для не-студентов: только обед, но он всегда посередине визуально (через CSS)
      const lunchCard = createLunchCard(lunches, date)
      if (lunchCard) {
        display.push(lunchCard)
      }
    }
    
    displayMenuByDate[date] = display
  })
  
  // Получаем даты сегодня и завтра
  // Используем локальное время для правильного определения сегодняшнего дня
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  
  // Форматируем даты в формате YYYY-MM-DD
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`
  
  // Меню на сегодня и завтра для отображения вверху
  // Важно: сегодняшний день всегда должен быть первым
  // Создаем массив с гарантированным порядком: сегодня первый, завтра второй
  const upcomingMenuDates: string[] = []
  // Сначала добавляем сегодняшний день (если есть)
  if (displayMenuByDate[todayStr]) {
    upcomingMenuDates.push(todayStr)
  }
  // Затем добавляем завтрашний день (если есть)
  if (displayMenuByDate[tomorrowStr]) {
    upcomingMenuDates.push(tomorrowStr)
  }
  
  // Отладочная информация
  console.log('Today date string:', todayStr)
  console.log('Tomorrow date string:', tomorrowStr)
  console.log('Available dates in displayMenuByDate:', Object.keys(displayMenuByDate))
  console.log('Upcoming menu dates:', upcomingMenuDates)
  
  // Исключаем сегодня и завтра из основного списка
  const historicalDates = Object.keys(displayMenuByDate).filter(
    date => date !== todayStr && date !== tomorrowStr
  )

  // Сортируем исторические даты (от новых к старым)
  const sortedHistoricalDates = historicalDates.sort((a, b) => {
    const dateA = new Date(a + 'T00:00:00').getTime()
    const dateB = new Date(b + 'T00:00:00').getTime()
    return dateB - dateA
  })
  
  // Функция для рендеринга секции с меню
  const renderMenuSection = (date: string, items: CanteenMenuItem[]) => {
    const dateStatus = getDateStatus(date)
    const isOnlyLunch = items.length === 1 && items[0].meal_type === 'lunch'
    
    return (
      <div key={date} className="date-section">
        <h2 className={`date-header date-${dateStatus}`}>
          {formatDate(date)}
          {dateStatus === 'today' && <span className="date-badge"> • Dnes</span>}
        </h2>
        <div className={`menu-items ${isOnlyLunch ? 'single-item' : ''}`}>
          {items.map(item => (
                <div key={item.id} className="menu-card">
                  <div className="menu-card-header">
                    <span className="meal-type-badge">
                      {getMealTypeIcon(item.meal_type)} {getMealTypeName(item.meal_type)}
                      {item.meal_type === 'lunch' && item.menu_number === 1 && ' - 1. chod'}
                      {item.meal_type === 'lunch' && item.menu_number === 2 && ' - 2. chod'}
                    </span>
                  </div>
                  
                  <h3 className="menu-item-name">{item.name}</h3>
                  
                  {item.description && (
                    <p className="menu-description">{item.description}</p>
                  )}
                  
                  <div className="menu-card-footer">
                    {item.can_vote ? (
                      <button 
                        className="vote-button"
                        onClick={() => openVoteModal(item)}
                      >
                        ⭐ Zanechat recenzi
                      </button>
                    ) : (
                      <span className="cannot-vote">Nelze ohodnotit</span>
                    )}
                  </div>
                </div>
              ))}
        </div>
      </div>
    )
  }

  return (
    <div className="public-canteen-voting">
      <Header
        leftButtons={
          <>
            <button
              onClick={() => navigate(-1)}
              className="back-button"
            >
              ← Zpět
            </button>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>🍽️ Hodnocení školní jídelny</h1>
          </>
        }
        rightContent={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {canSeeCanteenStats && (
              <button 
                className="statistics-button-header"
                onClick={() => navigate('/public-canteen/statistics')}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#0d9488',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                📊 Statistiky jídelny
              </button>
            )}
            <button 
              className="external-link-button-header"
              onClick={() => window.open('https://strav.nasejidelna.cz/0316/login', '_blank')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#14b8a6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              🔗 Web jídelny
            </button>
          </div>
        }
        showLogo={true}
      />
      <div className="canteen-container">
        <p className="subtitle" style={{ textAlign: 'center', marginTop: '0', marginBottom: '1rem' }}>Hodnocení je anonymní</p>
        <div className="info-box">
        <p>
          💡 <strong>Zobrazujeme jídla z posledních {menuData.days_limit} dní (včetně dnešního dne).</strong>
        </p>
        <p>
          Zanechte recenzi o kvalitě jídla a obsluhy. Každé jídlo můžete ohodnotit jednou.
        </p>
        <p className="info-hint">
          🔗 Budoucí jídelníček najdete na <a href="https://strav.nasejidelna.cz/0316/login" target="_blank" rel="noopener noreferrer">webu jídelny</a>
        </p>
      </div>

        {/* Меню на сегодня и завтра */}
        {upcomingMenuDates.length > 0 && (
          <div className="menu-by-dates upcoming-menu">
            {/* Сегодняшний день всегда первый - рендерим в явном порядке */}
            {upcomingMenuDates.map(date => renderMenuSection(date, displayMenuByDate[date]))}
          </div>
        )}

        {/* Историческое меню */}
        {sortedHistoricalDates.length > 0 && (
          <div className="menu-by-dates">
            {sortedHistoricalDates.map(date => renderMenuSection(date, displayMenuByDate[date]))}
          </div>
        )}
      </div>

      {/* Modal для отзыва */}
      {voteModalOpen && selectedMenuItem && (
        <div className="modal-overlay" onClick={closeVoteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ohodnotit jídlo</h2>
              <button className="close-button" onClick={closeVoteModal}>✕</button>
            </div>
            
            <div className="modal-body">
              <p className="meal-date">{formatDate(selectedMenuItem.date)}</p>
              
              {selectedMenuItem.meal_type === 'lunch' && (lunchVariants.lunch1 || lunchVariants.lunch2) ? (
                // Специальное отображение для обеда с выбором варианта
                <>
                  <div className="lunch-warning">
                    ⚠️ Vyberte jeden variant obědu a ohodnoťte ho
                  </div>
                  
                  <div className="lunch-variants">
                    {lunchVariants.lunch1 && (
                      <div 
                        className={`lunch-variant ${selectedLunchVariant === 1 ? 'selected' : ''}`}
                        onClick={() => setSelectedLunchVariant(1)}
                      >
                        <input 
                          type="radio" 
                          checked={selectedLunchVariant === 1}
                          onChange={() => setSelectedLunchVariant(1)}
                        />
                        <div className="variant-content">
                          <h4>Oběd 1. chod</h4>
                          <p>{lunchVariants.lunch1.name}</p>
                        </div>
                      </div>
                    )}
                    
                    {lunchVariants.lunch2 && (
                      <div 
                        className={`lunch-variant ${selectedLunchVariant === 2 ? 'selected' : ''}`}
                        onClick={() => setSelectedLunchVariant(2)}
                      >
                        <input 
                          type="radio" 
                          checked={selectedLunchVariant === 2}
                          onChange={() => setSelectedLunchVariant(2)}
                        />
                        <div className="variant-content">
                          <h4>Oběd 2. chod</h4>
                          <p>{lunchVariants.lunch2.name}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="rating-section">
                    <label>🍴 Kvalita jídla:</label>
                    <div className="star-selector">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          className={`star-button ${foodRating >= star ? 'selected' : ''}`}
                          onClick={() => setFoodRating(star)}
                          disabled={!selectedLunchVariant}
                        >
                          {foodRating >= star ? '⭐' : '☆'}
                        </button>
                      ))}
                    </div>
                    {foodRating > 0 && (
                      <span className="rating-label-text">{getRatingLabel(foodRating)}</span>
                    )}
                  </div>
                </>
              ) : (
                // Обычное отображение для других блюд
                <>
                  <h3>{selectedMenuItem.name}</h3>
                  
                  <div className="rating-section">
                    <label>🍴 Kvalita jídla:</label>
                    <div className="star-selector">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          className={`star-button ${foodRating >= star ? 'selected' : ''}`}
                          onClick={() => setFoodRating(star)}
                        >
                          {foodRating >= star ? '⭐' : '☆'}
                        </button>
                      ))}
                    </div>
                    {foodRating > 0 && (
                      <span className="rating-label-text">{getRatingLabel(foodRating)}</span>
                    )}
                  </div>
                </>
              )}
              
              <div className="rating-section">
                <label>👥 Kvalita obsluhy:</label>
                <div className="star-selector">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      className={`star-button ${serviceRating >= star ? 'selected' : ''}`}
                      onClick={() => setServiceRating(star)}
                    >
                      {serviceRating >= star ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>
                {serviceRating > 0 && (
                  <span className="rating-label-text">{getRatingLabel(serviceRating)}</span>
                )}
              </div>
              
              <div className="comment-input">
                <label>Komentář (volitelně):</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Co se vám líbilo/nelíbilo..."
                  rows={4}
                  maxLength={500}
                />
                <small>{comment.length}/500</small>
              </div>
              
              {/* Honeypot field - hidden from users, only bots fill it */}
              <input
                type="text"
                name="website"
                id="website"
                autoComplete="off"
                tabIndex={-1}
                style={{ 
                  position: 'absolute', 
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  opacity: 0
                }}
              />
            </div>
            
            <div className="modal-footer">
              <button className="cancel-button" onClick={closeVoteModal}>
                Zrušit
              </button>
              <button 
                className="submit-button" 
                onClick={submitVote}
                disabled={foodRating === 0 || serviceRating === 0 || submitting}
              >
                {submitting ? 'Odesílání...' : 'Odeslat recenzi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

